<template>
  <Teleport to="body">
    <Transition name="resume-prompt">
      <div
        v-if="visible && currentOrphan"
        class="resume-upload-prompt"
        role="dialog"
        aria-live="polite"
      >
        <div class="resume-upload-prompt__card">
          <div class="resume-upload-prompt__title">
            Resume previous upload?
          </div>
          <div class="resume-upload-prompt__detail">
            <span class="resume-upload-prompt__filename">{{
              currentOrphan.fileName
            }}</span>
            <span class="resume-upload-prompt__progress">
              {{ formatBytes(estimatedDoneBytes) }} / {{ formatBytes(currentOrphan.fileSize) }}
              ({{ Math.floor(estimatedPct) }}% already uploaded)
            </span>
          </div>
          <div v-if="errorMessage" class="resume-upload-prompt__error">
            {{ errorMessage }}
          </div>
          <div class="resume-upload-prompt__actions">
            <button
              type="button"
              class="resume-upload-prompt__btn resume-upload-prompt__btn--ghost"
              @click="discard"
            >
              Discard
            </button>
            <button
              type="button"
              class="resume-upload-prompt__btn"
              :disabled="resuming"
              @click="pickFile"
            >
              {{ resuming ? "Resuming…" : "Choose file & resume" }}
            </button>
          </div>
          <input
            ref="fileInputRef"
            type="file"
            class="resume-upload-prompt__file-input"
            :accept="accept"
            @change="handleFileChosen"
          />
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { storeToRefs } from "pinia";
import { useUploadQueueStore } from "@/stores/uploadQueue";

const store = useUploadQueueStore();
const { persistedOrphans } = storeToRefs(store);

const dismissed = ref<Set<string>>(new Set());
const resuming = ref(false);
const errorMessage = ref<string | null>(null);
const fileInputRef = ref<HTMLInputElement | null>(null);
const autoAttempted = ref<Set<string>>(new Set());

const currentOrphan = computed(() => {
  return persistedOrphans.value.find((o) => !dismissed.value.has(o.id)) ?? null;
});

// Try silent auto-resume from IDB blob first; only show picker when blob is missing.
watch(
  currentOrphan,
  async (orphan) => {
    if (!orphan || autoAttempted.value.has(orphan.id)) return;
    autoAttempted.value.add(orphan.id);
    try {
      const promise = await store.tryAutoResumeOrphan(orphan.id);
      if (promise) {
        await promise;
      }
    } catch (err) {
      errorMessage.value =
        err instanceof Error ? err.message : "Failed to resume upload";
    }
  },
  { immediate: true },
);

const visible = computed(() => currentOrphan.value !== null);

const accept = computed(() => {
  const orphan = currentOrphan.value;
  if (!orphan) return "*/*";
  if (orphan.fileType) return orphan.fileType;
  switch (orphan.mediaType) {
    case "video":
      return "video/*";
    case "image":
      return "image/*";
    case "audio":
      return "audio/*";
    default:
      return "*/*";
  }
});

const estimatedDoneBytes = computed(() => {
  const orphan = currentOrphan.value;
  if (!orphan) return 0;
  return Math.min(orphan.fileSize, orphan.completedParts.length * orphan.partSize);
});

const estimatedPct = computed(() => {
  const orphan = currentOrphan.value;
  if (!orphan || orphan.fileSize === 0) return 0;
  return (estimatedDoneBytes.value / orphan.fileSize) * 100;
});

function formatBytes(bytes: number): string {
  if (bytes >= 1024 * 1024 * 1024) {
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
  }
  if (bytes >= 1024 * 1024) {
    return `${(bytes / (1024 * 1024)).toFixed(0)} MB`;
  }
  if (bytes >= 1024) {
    return `${(bytes / 1024).toFixed(0)} KB`;
  }
  return `${bytes} B`;
}

function discard(): void {
  const orphan = currentOrphan.value;
  if (!orphan) return;
  store.discardOrphan(orphan.id);
  dismissed.value.delete(orphan.id);
}

function pickFile(): void {
  errorMessage.value = null;
  fileInputRef.value?.click();
}

async function handleFileChosen(event: Event): Promise<void> {
  const orphan = currentOrphan.value;
  const input = event.target as HTMLInputElement;
  const file = input.files?.[0];
  if (!file || !orphan) return;

  resuming.value = true;
  errorMessage.value = null;
  try {
    await store.resumeOrphan(orphan, file);
  } catch (err) {
    errorMessage.value =
      err instanceof Error ? err.message : "Failed to resume upload";
  } finally {
    resuming.value = false;
    input.value = "";
  }
}
</script>

<style lang="scss" scoped>
.resume-upload-prompt {
  position: fixed;
  bottom: 16px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 4000;
  width: min(420px, calc(100% - 32px));

  &__card {
    background: rgb(var(--v-theme-surface));
    color: rgb(var(--v-theme-on-surface));
    border: 1px solid rgba(var(--v-theme-on-surface), 0.12);
    border-radius: 12px;
    padding: 14px 16px;
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.32);
  }

  &__title {
    font-weight: 600;
    font-size: 0.95rem;
    margin-bottom: 6px;
  }

  &__detail {
    display: flex;
    flex-direction: column;
    gap: 2px;
    font-size: 0.8rem;
    color: rgba(var(--v-theme-on-surface), 0.78);
    margin-bottom: 10px;
  }

  &__filename {
    font-weight: 500;
    word-break: break-all;
  }

  &__error {
    font-size: 0.75rem;
    color: rgb(239, 68, 68);
    margin-bottom: 8px;
  }

  &__actions {
    display: flex;
    gap: 8px;
    justify-content: flex-end;
  }

  &__btn {
    appearance: none;
    border: none;
    border-radius: 8px;
    padding: 8px 14px;
    font-size: 0.85rem;
    font-weight: 600;
    cursor: pointer;
    background: rgb(var(--v-theme-primary));
    color: rgb(var(--v-theme-on-primary));

    &:disabled {
      opacity: 0.6;
      cursor: progress;
    }

    &--ghost {
      background: transparent;
      color: rgb(var(--v-theme-on-surface));
      border: 1px solid rgba(var(--v-theme-on-surface), 0.18);
    }
  }

  &__file-input {
    display: none;
  }
}

.resume-prompt-enter-active,
.resume-prompt-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.resume-prompt-enter-from,
.resume-prompt-leave-to {
  opacity: 0;
  transform: translate(-50%, 12px);
}
</style>
